import classNames from 'classnames';
import { useCallback, useEffect, useState } from 'react';
import { FaAngleRight } from 'react-icons/fa';
import { MdPerson } from 'react-icons/md';
import CircleIcon from 'components/CircleIcon';
import DataItem from 'components/DataItem';
import SectionLabel from 'components/typography/SectionLabel';
import useWhiteLabelSettings from 'hooks/useWhiteLabelSettings';
import { getClientApis } from 'services/ClientService';
import { ROUTES_SELF_SERVICE } from 'shared/constants/selfServiceRoutes';
import {
  displayDate,
  getFullname,
  getGenderDisplay,
} from 'shared/helpers/common';
import { DEFAULT_PRIMARY_COLOUR } from 'shared/constants/styleConstants';
import ViewMoreDetails from './ViewMoreDetails';

export default function Passport() {
  const [passportDisplay, setPassportDisplay] = useState({});
  const { whiteLabelSettings: { primaryColour } = {} } =
  useWhiteLabelSettings();
  const backgroundStyle = {
    backgroundColor: primaryColour || DEFAULT_PRIMARY_COLOUR,
  };

  const loadData = useCallback(async () => {
    try {
      const { data } = await getClientApis();
      setPassportDisplay(data?.results?.[0] || {});
    } catch (error) {
      setPassportDisplay({});
      console.error(error);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const { first: firstName, last: lastName } = passportDisplay?.name || {};
  const fullname = getFullname({ firstName, lastName });

  return (
    <>
      <div className="mt-[40px] flex justify-between items-center">
        <div className="flex items-start">
          <CircleIcon className="mt-1">
            <MdPerson style={{ fill: primaryColour }} />
          </CircleIcon>
          <SectionLabel label="Your passport" className="ml-[10px] mt-1" />
        </div>
        <ViewMoreDetails
          className="hidden lg:flex"
          label="View all documents"
          icon={<FaAngleRight />}
          url={ROUTES_SELF_SERVICE.DOCUMENTS}
        />
      </div>
      <div className="mt-[40px] w-full">
        <div
          style={backgroundStyle}
          className="mt-[40px] rounded-t-lg text-white text-xl font-bold"
        >
          <div className="p-4 flex items-start">
            <div
              className={`${classNames(
                'text-xl font-bold flex items-center w-full',
                {
                  'p-3': !fullname,
                },
              )}`}
            >
              {fullname}
            </div>
          </div>
        </div>
        <div className="rounded-b-[10px] bg-white w-full flex flex-wrap p-4">
          <DataItem label="Citizenship" value={passportDisplay?.citizenship} />
          <DataItem
            label="Country of residence"
            value={passportDisplay?.countryOfResidence}
          />
          <DataItem
            label="Date of birth"
            value={displayDate(passportDisplay?.dateOfBirth, '-', undefined, true)}
          />
          <DataItem
            label="Passport number"
            value={passportDisplay?.passportNumber}
          />
          <DataItem
            label="Passport issue location"
            value={passportDisplay?.passportIssueLocation}
          />

          <DataItem
            label="Passport issue date"
            value={displayDate(passportDisplay?.passportIssue, '-', undefined, true)}
          />
          <DataItem
            label="Passport expiry date"
            value={displayDate(passportDisplay?.passportExpiry, '-', undefined, true)}
          />
          <DataItem
            label="Gender"
            value={getGenderDisplay(passportDisplay?.gender)}
          />
          <DataItem
            label="Destination address"
            value={passportDisplay?.destinationAddress}
            containerClassName="my-3 grow"
          />
        </div>
      </div>
      <ViewMoreDetails
        className="mt-7 lg:hidden"
        label="View all documents"
        icon={<FaAngleRight />}
        url={ROUTES_SELF_SERVICE.DOCUMENTS}
        isSmallScreen
      />
    </>
  );
}
